import { ComponentType } from "react";

/**
 * Wraps a component to use a prop value as the React key, forcing remount when that prop changes.
 * Similar to `forwardRef` or `memo`, but for resetting component state.
 *
 * This is useful when you want all internal component state (useState, useRef, etc.) to reset
 * when a specific prop value changes, without having to manually manage dependencies or state cleanup.
 *
 * @template P - Props type that must be a record with string keys
 * @param Component - The component to wrap
 * @param key - The prop name or callback function to determine the React key
 * @returns A wrapped component that remounts when the keyProp value changes
 *
 * @example
 * ```tsx
 * interface ThreadViewProps {
 *   threadId: string;
 *   userId: string;
 * }
 *
 * // Component will completely remount when threadId changes, resetting all internal state
 * export const ThreadView = withKey<ThreadViewProps>(
 *   ({ threadId, userId }) => {
 *     const [isExpanded, setIsExpanded] = useState(false);
 *     // isExpanded will reset to false when threadId changes
 *     return <div>{threadId}</div>;
 *   },
 *   'threadId'
 * );
 * ```
 *
 * @example
 * ```tsx
 * // With named function for better debugging
 * export const MessageList = withKey<MessageListProps>(
 *   function MessageListInternal({ conversationId }) {
 *     const [messages, setMessages] = useState([]);
 *     // Component state resets when conversationId changes
 *     return <div>{messages.map(m => <div key={m.id}>{m.text}</div>)}</div>;
 *   },
 *   'conversationId'
 * );
 * ```
 */
export function withKey<P extends object>(
  Component: ComponentType<P>,
  key: keyof P | ((props: P) => string)
): ComponentType<P> {
  const WrappedComponent = (props: P) => {
    const keyValue = typeof key === "function" ? key(props) : props[key];
    return <Component key={String(keyValue)} {...props} />;
  };

  WrappedComponent.displayName = `WithKey(${Component.displayName || Component.name || "Component"})`;

  return WrappedComponent;
}
